import { RadioControlGroup, RadioInput, replaceStr, Select } from 'flos-react-cvi';
import React from 'react';
import { connect } from 'react-redux';
import { Box } from '../components/box';
import { CompanySelect } from '../components/company-select';
import { DateField } from '../components/date-field';
import {
    setInsuranceCompany,
    setInsuranceTerminateDate,
    setNewInsuranceStartDate,
    setInsuranceNumber
} from '../store/actions';
import {
    selectCurrentInsuranceDetails,
    selectFormMetadata,
    selectHasSelectedInsuranceType,
    selectIsPrimaryType,
    selectNewInsuranceStartDate,
    selectResources,
    selectShouldShowNewDateInput,
    selectCurrentInsuranceTypeIsChecked,
    selectHasContentInsurance
} from '../store/selectors';
import { setShouldTerminateAndShowDateInput, setExistingContentInsurance } from '../store/thunks';
import { InsuranceNumberField } from '../components/insurance-number-field';

const CurrentInsuranceDetailsView = ({
    insuranceType,
    isSelected,
    hasContentsInsurance,
    resources,
    isPrimary,
    setShouldTerminate,
    setInsuranceCompanyProp,
    setInsuranceNumberProp,
    setHasExistingContentInsurance,
    setInsuranceTerminateDateProp,
    newInsuranceStartDate,
    setNewInsuranceStartDateProp,
    shouldShowNewDateInput,
    hasSubmitted,
    isChecked,
    details: { shouldTerminate, currentInsuranceCompany, currentInsuranceNumber, terminationDate },
    meta: { closestTerminationDateDisplay, currentDate, terminationDateOptions, closestTerminationDate }
}) => {
    const [showTerminateDateOptions, setShowTerminateDateOptions] = React.useState(false);
    const [showInsuranceNumberField, setShowInsuranceNumberField] = React.useState(false);
    const labels = useLabels(resources, insuranceType, closestTerminationDateDisplay);
    return isSelected ? (
        <Box title={labels.title} icon={labels.icon}>
            {insuranceType === 'contents' && (
                <RadioControlGroup
                    value={hasContentsInsurance}
                    onChangeValue={setHasExistingContentInsurance}
                    label={labels.currentContentsInsuranceExistsLabel}
                    status={hasSubmitted && !isChecked ? 'error' : null}
                    required
                >
                    <RadioInput
                        value={true}
                        label={labels.hasCurrentContentsInsuranceAccept}
                        data-testid={`hasContentInsurance.true`}
                    />
                    <RadioInput
                        value={false}
                        label={labels.hasCurrentContentsInsuranceDeny}
                        data-testid={`hasContentInsurance.false`}
                    />
                </RadioControlGroup>
            )}
            {((insuranceType === 'contents' && hasContentsInsurance) || insuranceType === 'accident') && (
                <RadioControlGroup
                    value={shouldTerminate}
                    onChangeValue={setShouldTerminate}
                    label={labels.terminateInsurance}
                    status={hasSubmitted && !isChecked ? 'error' : null}
                    required
                >
                    <RadioInput
                        value={true}
                        label={labels.terminateInsuranceTrue}
                        data-testid={`${insuranceType}Terminate.true`}
                    />
                    <RadioInput
                        value={false}
                        label={labels.terminateInsuranceFalse}
                        data-testid={`${insuranceType}Terminate.false`}
                    />
                </RadioControlGroup>
            )}
            {shouldTerminate && (
                <>
                    <RadioControlGroup
                        value={showTerminateDateOptions}
                        onChangeValue={setShowTerminateDateOptions}
                        label={labels.terminateDate}
                        description={labels.terminateDateDescription}
                        required
                    >
                        <RadioInput
                            value={false}
                            onClick={() => setInsuranceTerminateDateProp(closestTerminationDate)}
                            label={labels.terminateDateAsap}
                            data-testid={`${insuranceType}TerminateDate.asap`}
                        />
                        <RadioInput
                            value={true}
                            label={labels.terminateDateSpecific}
                            data-testid={`${insuranceType}TerminateDate.specific`}
                        />
                    </RadioControlGroup>
                    {showTerminateDateOptions && (
                        <div className="form-group">
                            <div className="row">
                                <div className="col-xs-12 col-sm-6">
                                    <Select
                                        value={terminationDate}
                                        onChangeValue={setInsuranceTerminateDateProp}
                                        options={terminationDateOptions}
                                        data-testid={`${insuranceType}TerminateDateSelect`}
                                        className="termination-date"
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                    <p className="u-Space-up">{labels.cancellationFeeNote}</p>
                    <div className="row">
                        <div className="col-sm-6 col-xs-12" style={{ marginBottom: '15px' }}>
                            <CompanySelect
                                value={currentInsuranceCompany}
                                onChangeValue={setInsuranceCompanyProp}
                                label={labels.insuranceCompany}
                                required
                                data-testid={`${insuranceType}CurrentCompany`}
                                class="select-current-insurance-company"
                                showNumberField={setShowInsuranceNumberField}
                            />
                        </div>
                        <div className="col-sm-6 col-xs-12" style={{ marginBottom: '15px' }}>
                            {showInsuranceNumberField && (
                                <InsuranceNumberField
                                    insuranceType={insuranceType}
                                    label={labels.insuranceNumber}
                                    dialogLabel={labels.insuranceNumberTooltip}
                                    value={currentInsuranceNumber}
                                    onChange={setInsuranceNumberProp}
                                />
                            )}
                        </div>
                    </div>
                </>
            )}
            {shouldTerminate === false &&
                (shouldShowNewDateInput ? (
                    <DateField
                        value={newInsuranceStartDate}
                        onChangeValue={setNewInsuranceStartDateProp}
                        minDate={currentDate}
                        label={resources.currentInsuranceNewStartDateLabel}
                        id={`${insuranceType}-new-start-date`}
                        required
                    />
                ) : (
                    <p>
                        <b>{resources.currentInsuranceNewStartDateSameDayNote}</b>
                    </p>
                ))}
        </Box>
    ) : null;
};

const useLabels = (resources, insuranceType, closestTerminationDateDisplay) =>
    React.useMemo(() => {
        const isAccidentInsurance = insuranceType === 'accident';
        return {
            title: isAccidentInsurance
                ? resources.currentAccidentInsuranceTitle
                : resources.currentContentsInsuranceTitle,
            icon: isAccidentInsurance ? resources.currentAccidentInsuranceIcon : resources.currentContentsInsuranceIcon,
            terminateInsurance: isAccidentInsurance
                ? resources.currentAccidentInsuranceTerminateLabel
                : resources.cancelCurrentContentInsuranceStepTwo,
            terminateInsuranceTrue: isAccidentInsurance
                ? resources.currentAccidentInsuranceTerminateOptionYes
                : resources.currentContentsInsuranceTerminateOptionYes,
            terminateInsuranceFalse: isAccidentInsurance
                ? resources.currentAccidentInsuranceTerminateOptionNo
                : resources.currentContentsInsuranceTerminateOptionNo,
            terminateDate: isAccidentInsurance
                ? resources.currentAccidentInsuranceTimeLabel
                : resources.currentContentsInsuranceTimeLabel,
            terminateDateDescription: isAccidentInsurance
                ? resources.currentAccidentInsuranceTimeDescription
                : resources.currentContentsInsuranceTimeDescription,
            terminateDateAsap: isAccidentInsurance
                ? replaceStr(resources.currentAccidentInsuranceTimeOptionAsap, {
                      '{closestDate}': closestTerminationDateDisplay
                  })
                : replaceStr(resources.currentContentsInsuranceTimeOptionAsap, {
                      '{closestDate}': closestTerminationDateDisplay
                  }),
            terminateDateSpecific: isAccidentInsurance
                ? resources.currentAccidentInsuranceTimeOptionSpecificDate
                : resources.currentContentsInsuranceTimeOptionSpecificDate,
            cancellationFeeNote: isAccidentInsurance
                ? resources.currentAccidentInsuranceCancellationFeeNote
                : resources.currentContentsInsuranceCancellationFeeNote,
            insuranceCompany: isAccidentInsurance
                ? resources.currentAccidentInsuranceCompanyLabel
                : resources.currentContentsInsuranceCompanyLabel,
            insuranceNumber: isAccidentInsurance
                ? resources.currentAccidentInsuranceNumberLabel
                : resources.currentContentsInsuranceNumberLabel,
            insuranceNumberTooltip: isAccidentInsurance
                ? resources.currentAccidentInsuranceNumberTooltip
                : resources.currentContentsInsuranceNumberTooltip,
            currentContentsInsuranceExistsLabel: resources.currentContentsInsuranceExistsLabel,
            hasCurrentContentsInsuranceAccept: resources.hasCurrentContentsInsuranceAccept,
            hasCurrentContentsInsuranceDeny: resources.hasCurrentContentsInsuranceDeny
        };
    }, [resources, insuranceType, closestTerminationDateDisplay]);

const mapStates = (state, { insuranceType }) => {
    return {
        resources: selectResources(state),
        isPrimary: selectIsPrimaryType(state, insuranceType),
        details: selectCurrentInsuranceDetails(state, insuranceType),
        newInsuranceStartDate: selectNewInsuranceStartDate(state),
        meta: selectFormMetadata(state),
        shouldShowNewDateInput: selectShouldShowNewDateInput(state, insuranceType),
        isSelected: selectHasSelectedInsuranceType(state, insuranceType),
        isChecked: selectCurrentInsuranceTypeIsChecked(state, insuranceType),
        hasContentsInsurance: selectHasContentInsurance(state)
    };
};

const mapDispatch = (dispatch, { insuranceType }) => ({
    setHasExistingContentInsurance: hasContentsInsurance =>
        dispatch(setExistingContentInsurance(insuranceType, hasContentsInsurance)),
    setShouldTerminate: shouldTerminate => dispatch(setShouldTerminateAndShowDateInput(insuranceType, shouldTerminate)),
    setInsuranceTerminateDateProp: terminationDate =>
        dispatch(setInsuranceTerminateDate(insuranceType, terminationDate)),
    setInsuranceCompanyProp: (companyId, companyName) =>
        dispatch(setInsuranceCompany(insuranceType, companyId, companyName)),
    setInsuranceNumberProp: number => dispatch(setInsuranceNumber(insuranceType, number)),
    setNewInsuranceStartDateProp: startDate => dispatch(setNewInsuranceStartDate(startDate))
});

export const CurrentInsuranceDetails = connect(mapStates, mapDispatch)(CurrentInsuranceDetailsView);
